import { useEffect, useRef } from 'react';
import { cn } from '../lib/classnames.js';

/**
 * MoltenGold — a contained pool of living gold meant to sit BEHIND a card (the
 * balance block). The looping video is the material; a WebGL pass makes the
 * GOLD'S OWN reflections the reactive part:
 *   - at rest the surface is dimmed, moody (so the card's type reads);
 *   - where you hover / drag, the video's existing bright specular highlights
 *     FLARE UP to full brilliance, and a short trail of that flare follows your
 *     movement — it's the real shine in the footage igniting under your pointer,
 *     not a glow drawn on top.
 * Cross-origin video is read as a texture (worker serves ACAO:*); if WebGL is
 * unavailable it falls back to the plain dimmed video.
 */
const MOLTEN_MP4 = 'https://api.gilding.app/images/molten/molten-v1.mp4';
const MOLTEN_POSTER = 'https://api.gilding.app/images/molten/molten-v1-poster.jpg';

const TRAIL = 5; // history points behind the live pointer
const PTS = TRAIL + 1; // + the live pointer itself

const VERT = `attribute vec2 aPos; void main(){ gl_Position = vec4(aPos,0.0,1.0); }`;

const FRAG = `
precision highp float;
uniform sampler2D uTex;
uniform vec2 uRes;
uniform vec2 uTexSize;
uniform vec3 uPts[${PTS}];  // xy = point (device px, top-left), z = weight
uniform float uR;           // flare radius (device px)

void main(){
  vec2 px = vec2(gl_FragCoord.x, uRes.y - gl_FragCoord.y);

  // cover-fit the video
  float ca = uRes.x / uRes.y;
  float ta = uTexSize.x / uTexSize.y;
  vec2 uv = px / uRes;
  vec2 sc = vec2(1.0);
  if (ca > ta) sc.y = ta / ca; else sc.x = ca / ta;
  vec3 raw = texture2D(uTex, (uv - 0.5) * sc + 0.5).rgb;

  // how bright is THIS pixel's gold? (only real highlights should flare)
  float lum = max(raw.r, max(raw.g, raw.b));

  // proximity to the pointer + its trail
  float prox = 0.0;
  for (int i = 0; i < ${PTS}; i++) {
    vec3 p = uPts[i];
    if (p.z <= 0.001) continue;
    float d = distance(px, p.xy);
    prox += exp(-(d * d) / (2.0 * uR * uR)) * p.z;
  }
  prox = min(prox, 1.6);

  vec3 col = raw * 0.42;                          // moody at rest (and legible)
  col += raw * pow(lum, 1.7) * prox * 2.7;       // the footage's own shine flares under the pointer
  gl_FragColor = vec4(col, 1.0);
}`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    gl.deleteShader(s);
    return null;
  }
  return s;
}

export function MoltenGold({ className }: { className?: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = 0.6;
    const p = v.play();
    if (typeof p?.catch === 'function') void p.catch(() => undefined);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !video || !canvas) return;
    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: false });
    if (!gl) {
      canvas.style.display = 'none';
      return;
    }
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const prog = gl.createProgram();
    if (!vs || !fs || !prog) {
      canvas.style.display = 'none';
      return;
    }
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      canvas.style.display = 'none';
      return;
    }
    gl.useProgram(prog);
    gl.clearColor(0, 0, 0, 0);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uRes = gl.getUniformLocation(prog, 'uRes');
    const uTexSize = gl.getUniformLocation(prog, 'uTexSize');
    const uPts = gl.getUniformLocation(prog, 'uPts');
    const uR = gl.getUniformLocation(prog, 'uR');

    const DPR = Math.min(window.devicePixelRatio || 1, 1.4);
    let cw = 1;
    let ch = 1;
    let radius = 200;
    const resize = (): void => {
      const r = wrap.getBoundingClientRect();
      cw = Math.max(1, Math.round(r.width * DPR));
      ch = Math.max(1, Math.round(r.height * DPR));
      canvas.width = cw;
      canvas.height = ch;
      gl.viewport(0, 0, cw, ch);
      radius = Math.min(cw, ch) * 0.34;
    };
    resize();

    // pointer (device px, top-left) + a short decaying trail
    const data = new Float32Array(PTS * 3);
    const cursor = { x: 0, y: 0, active: false };
    let tw = 0;
    let lastTrailX = -1e9;
    let lastTrailY = -1e9;

    const update = (clientX: number, clientY: number): void => {
      const r = wrap.getBoundingClientRect();
      const x = (clientX - r.left) * DPR;
      const y = (clientY - r.top) * DPR;
      const m = 56 * DPR;
      if (x < -m || y < -m || x > cw + m || y > ch + m) {
        cursor.active = false;
        return;
      }
      cursor.x = x;
      cursor.y = y;
      cursor.active = true;
      // lay a trail point every ~16 device px so a drag leaves a comet of flare
      if (Math.hypot(x - lastTrailX, y - lastTrailY) > 16 * DPR) {
        const o = (1 + tw) * 3; // slots 1..TRAIL are the trail; slot 0 is the live point
        data[o] = x;
        data[o + 1] = y;
        data[o + 2] = 1;
        tw = (tw + 1) % TRAIL;
        lastTrailX = x;
        lastTrailY = y;
      }
    };

    let raf = 0;
    let textured = false;
    const render = (): void => {
      raf = requestAnimationFrame(render);
      gl.clear(gl.COLOR_BUFFER_BIT);
      if (video.readyState >= 2) {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        try {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
          textured = true;
        } catch {
          cancelAnimationFrame(raf);
          canvas.style.display = 'none'; // tainted/decoding — show the plain video
          return;
        }
      }
      if (!textured) return;
      // live pointer in slot 0; decay the trail slots
      data[0] = cursor.x;
      data[1] = cursor.y;
      data[2] = cursor.active ? 1 : 0;
      for (let i = 1; i < PTS; i += 1) {
        const k = i * 3 + 2;
        data[k] = (data[k] ?? 0) * 0.9;
      }
      gl.uniform2f(uRes, cw, ch);
      gl.uniform2f(uTexSize, video.videoWidth || 1280, video.videoHeight || 960);
      gl.uniform1f(uR, radius);
      gl.uniform3fv(uPts, data);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const onPointer = (e: PointerEvent): void => {
      if (e.pointerType === 'touch') return;
      update(e.clientX, e.clientY);
    };
    const onTouch = (e: TouchEvent): void => {
      const t = e.touches[0];
      if (t) update(t.clientX, t.clientY);
    };
    const onOut = (): void => {
      cursor.active = false;
    };

    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('touchend', onOut, { passive: true });
    window.addEventListener('touchcancel', onOut, { passive: true });
    window.addEventListener('resize', resize, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(wrap);
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('touchend', onOut);
      window.removeEventListener('touchcancel', onOut);
      window.removeEventListener('resize', resize);
      ro?.disconnect();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return (
    <div ref={wrapRef} className={cn('pointer-events-none isolate overflow-hidden', className)} aria-hidden="true">
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        src={MOLTEN_MP4}
        poster={MOLTEN_POSTER}
        crossOrigin="anonymous"
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
      />
      {/* Dims the plain video for the no-WebGL fallback; hidden behind the canvas
          when the shader is running (it dims in-shader). */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(7,5,11,0.58)' }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
